import React, { useMemo } from 'react'
import type { HexItem } from '@/hooks/useHexItems'
import type { ExpansionPhase } from '@/hooks/useHexExpansion'
import { useCatalogStore } from '@/store/catalogStore'
import {
  BG_DURATION_MS,
  CLOSE_EASE,
  CLOSE_FADE_MS,
  CLOSE_REFLOW_MS,
  EXPAND_EASE,
  REFLOW_MS,
} from './expandAnim'
import { HexLayerStack } from './HexLayerStack'
import { HexExpandedRow } from './HexExpandedRow'
import { expandSummaryFromItem } from './expandSummary'

interface Props {
  item: HexItem
  selected?: boolean
  expanded: boolean
  phase: ExpansionPhase
  /** Raw hook phase for the whole grid (drives close-reflow timing). */
  gridPhase: ExpansionPhase
  canDrill: boolean
  x: number
  y: number
  width: number
  height: number
  onToggle: () => void
  onDrill: () => void
  onClose: () => void
}

export function HexTile({
  item,
  selected,
  expanded,
  phase,
  gridPhase,
  canDrill,
  x,
  y,
  width,
  height,
  onToggle,
  onDrill,
  onClose,
}: Props) {
  const studios = useCatalogStore((s) => s.studios)
  const studioNames = useMemo(
    () => new Map(studios.map((s) => [s.code, s.name])),
    [studios],
  )
  const summary = useMemo(
    () => expandSummaryFromItem(item, studioNames),
    [item, studioNames],
  )

  const closing = expanded && phase === 'closing'

  // Keep the layered hex mounted under the expanded chrome so close can
  // crossfade smoothly (no remount flash).
  const showCollapsed =
    !expanded
    || phase === 'push'
    || phase === 'bg_in'
    || phase === 'inner'
    || phase === 'text'
    || phase === 'arrow'
    || phase === 'open'
    || closing

  const collapsedFade =
    !expanded ? 1
    : phase === 'push' || closing ? 1
    : 0

  const showExpanded = expanded && phase !== 'push' && phase !== 'reflow'
  const expandedFade = closing ? 0 : 1

  const fadeMs = closing ? CLOSE_FADE_MS : BG_DURATION_MS
  const fadeEase = closing ? CLOSE_EASE : EXPAND_EASE

  // While the slot is wide (any expanded phase, incl. push/closing), pin the
  // origin layered hex to the LEFT of the band. The band then grows rightward
  // on open and collapses left-to-origin on close, so siblings simply slide
  // back into their original positions. Its center (CELL_W/2) lines up with the
  // first expanded background hex center (bgW/2), so nothing jumps.
  const anchorLeft = expanded

  // The gap-closing reflow runs slower than the open push so the collapse
  // reads as its own smooth motion after the crossfade.
  const reflowMs = gridPhase === 'reflow' ? CLOSE_REFLOW_MS : REFLOW_MS

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width,
        height,
        transition: `left ${reflowMs}ms ${CLOSE_EASE}, top ${reflowMs}ms ${CLOSE_EASE}, width ${reflowMs}ms ${CLOSE_EASE}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        boxSizing: 'border-box',
      }}
      onClick={expanded && !closing ? onClose : undefined}
    >
      {showCollapsed && (
        <div
          style={{
            position: anchorLeft ? 'absolute' : 'relative',
            left: anchorLeft ? 0 : undefined,
            top: anchorLeft ? '50%' : undefined,
            transform: anchorLeft ? 'translateY(-50%)' : undefined,
            opacity: collapsedFade,
            transition: `opacity ${fadeMs}ms ${fadeEase}`,
            pointerEvents: expanded ? 'none' : 'auto',
            zIndex: closing ? 3 : 1,
          }}
        >
          <HexLayerStack
            id={item.id}
            label={item.label}
            tick={item.tick}
            selected={selected}
            fade={1}
            onToggleExpand={onToggle}
          />
        </div>
      )}

      {expanded && showExpanded && (
        <div
          style={{
            width: '100%',
            height: '100%',
            opacity: expandedFade,
            transition: `opacity ${fadeMs}ms ${fadeEase}`,
            zIndex: 2,
            pointerEvents: closing ? 'none' : 'auto',
          }}
        >
          <HexExpandedRow
            summary={summary}
            tick={item.tick}
            phase={phase}
            bandWidth={width}
            canDrill={canDrill}
            onDrill={onDrill}
          />
        </div>
      )}
    </div>
  )
}
